/**
 * Wo das Browserfenster aufgeht. KMVS merkt sich Lage und Größe des zuletzt
 * geschlossenen — so öffnet das nächste dort, wohin der Nutzer es gezogen hat,
 * typischerweise auf dem zweiten Bildschirm.
 *
 * Der Browser hält ein neues Fenster allerdings auf dem Bildschirm des
 * Hauptfensters fest, dann muss man es einmal hinüberziehen. Mehr erlaubt er
 * erst mit „Fenster auf allen Bildschirmen verwalten“ — danach fragt KMVS
 * bewusst nicht (User 27.10.2026: „Immer auf diesem Bildschirm öffnen
 * rausnehmen“).
 */
export type Geometrie = { left: number; top: number; width: number; height: number };

const SCHLUESSEL = "kmvs.browserFenster.geometrie";
const MIN_BREITE = 420;
const MIN_HOEHE = 320;

const zahl = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

export function ladeGeometrie(): Geometrie | null {
  try {
    const roh = JSON.parse(localStorage.getItem(SCHLUESSEL) ?? "null");
    if (roh && zahl(roh.left) && zahl(roh.top) && zahl(roh.width) && zahl(roh.height)) {
      return {
        left: Math.round(roh.left),
        top: Math.round(roh.top),
        width: Math.max(MIN_BREITE, Math.round(roh.width)),
        height: Math.max(MIN_HOEHE, Math.round(roh.height)),
      };
    }
  } catch {
    // privater Modus o. Ä.
  }
  return null;
}

export function merkeGeometrie(popup: Window) {
  try {
    if (popup.closed) return;
    const g: Geometrie = {
      left: popup.screenX,
      top: popup.screenY,
      width: popup.innerWidth,
      height: popup.innerHeight,
    };
    if (g.width < 100 || g.height < 100) return;
    localStorage.setItem(SCHLUESSEL, JSON.stringify(g));
  } catch {
    // privater Modus o. Ä.
  }
}

/**
 * Ohne gemerkte Lage: dort, wo das schwebende Fenster gerade steht —
 * `layer` ist der Fenster-Layer im Hauptfenster (Viewport-Koordinaten).
 */
export function geometrieAmFenster(
  layer: { left: number; top: number },
  fenster: { position: { x: number; y: number }; size: { width: number; height: number } },
  haupt: Pick<Window, "screenX" | "screenY" | "outerHeight" | "innerHeight" | "outerWidth" | "innerWidth">,
): Geometrie {
  // Rahmen und Werkzeugleisten des Hauptfensters liegen oberhalb des Viewports.
  const randOben = Math.max(0, haupt.outerHeight - haupt.innerHeight);
  const randLinks = Math.max(0, (haupt.outerWidth - haupt.innerWidth) / 2);
  return {
    left: Math.round(haupt.screenX + randLinks + layer.left + fenster.position.x),
    top: Math.round(haupt.screenY + randOben + layer.top + fenster.position.y),
    width: Math.max(MIN_BREITE, Math.round(fenster.size.width)),
    height: Math.max(MIN_HOEHE, Math.round(fenster.size.height)),
  };
}

export function fensterMerkmale(g: Geometrie): string {
  return `popup=yes,left=${g.left},top=${g.top},width=${g.width},height=${g.height}`;
}

/**
 * Neben einem schon offenen Browserfenster, leicht versetzt — so geht ein
 * daraus geöffnetes Fenster (Vorschau) auf demselben Bildschirm auf.
 * `null`, wenn es keins (mehr) gibt.
 */
export function geometrieNeben(
  quelle: Pick<Window, "closed" | "screenX" | "screenY" | "innerWidth" | "innerHeight"> | null | undefined,
): Geometrie | null {
  try {
    if (!quelle || quelle.closed) return null;
    return {
      left: Math.round(quelle.screenX + 32),
      top: Math.round(quelle.screenY + 32),
      width: Math.max(MIN_BREITE, Math.round(quelle.innerWidth)),
      height: Math.max(MIN_HOEHE, Math.round(quelle.innerHeight)),
    };
  } catch {
    return null;
  }
}
